import { useEffect } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CircleCheckBigIcon,
  Flex,
  IconButton,
} from '@buffer-mono/popcorn'

import { useDismissMention } from '../../hooks/engagement/useDismissMention'
import { useUndismissMention } from '../../hooks/engagement/useUndismissMention'
import { useMentionData } from '../context/MentionContext'

import styles from './MentionActions.module.css'

type MentionActionsProps = {
  isHovered?: boolean
  isLocallyDismissed?: boolean
  isLocallyReplied?: boolean
  isInterimExpanded?: boolean
  disabled?: boolean
  expanded?: boolean
  onLoadingChange?: (loading: boolean) => void
  onCollapse?: () => void
  onExpandNextMention?: () => void
  onDismissed?: () => void
  onUndismissed?: () => void
}

/**
 * Right-rail action buttons for a `MentionCard`. Mirror of `CommentActions`:
 * owns the dismiss / undo mutations (`useDismissMention` /
 * `useUndismissMention`) and only notifies the parent (`onDismissed` /
 * `onUndismissed`) once the mutation succeeds — the card flips pessimistically
 * via the list-owned resolution set.
 */
export const MentionActions = ({
  isHovered = false,
  isLocallyDismissed = false,
  isLocallyReplied = false,
  isInterimExpanded = false,
  disabled = false,
  expanded = false,
  onLoadingChange,
  onCollapse,
  onExpandNextMention,
  onDismissed,
  onUndismissed,
}: MentionActionsProps): JSX.Element => {
  const mention = useMentionData()
  const { dismissMention, loading: isDismissing } = useDismissMention()
  const { undismissMention, loading: isUndismissing } = useUndismissMention()

  const isLoading = isDismissing || isUndismissing
  const isDismissed = mention.status === 'dismissed' || isLocallyDismissed
  const isReplied = mention.status === 'replied' || isLocallyReplied

  // Visibility mirrors `CommentActions.shouldShowActions`: dismissed cards keep
  // their reopen affordance permanently, everything else reveals on hover or
  // when expanded.
  const isVisible = isDismissed || isHovered || expanded

  // Notify parent of loading state changes (mirror of CommentActions).
  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleDismiss = async (): Promise<void> => {
    const success = await dismissMention(mention.id)
    // Only flip the card once the BE confirms the dismiss.
    if (success) {
      onDismissed?.()

      // Dismissing the expanded card collapses it and opens the next repliable
      // mention. A hover-dismiss on a non-expanded card leaves the open one be.
      if (expanded) {
        onCollapse?.()
        onExpandNextMention?.()
      }
    }
  }

  const handleUndismiss = async (): Promise<void> => {
    const success = await undismissMention(mention.id)
    // Only restore the card once the BE confirms the undismiss.
    if (success) {
      onUndismissed?.()
    }
  }

  const finalDisabled = disabled || isLoading

  return (
    <Flex
      direction="column"
      gap="xs"
      className={isVisible ? styles.visible : styles.hidden}
      data-testid="mention-actions"
    >
      {isDismissed && (
        <>
          <IconButton
            label="Reopen this mention"
            onClick={() => {
              void handleUndismiss()
            }}
            tooltip="Undo dismiss"
            variant={isInterimExpanded || expanded ? 'secondary' : 'tertiary'}
            className={`${
              isInterimExpanded
                ? styles.dismissButton
                : styles.undoDismissButton
            } ${styles.desktopOnly}`}
            aria-label="Reopen this mention"
            disabled={finalDisabled}
            data-expanded={expanded ? 'true' : 'false'}
          >
            <CircleCheckBigIcon />
          </IconButton>
          <Button
            onClick={() => {
              void handleUndismiss()
            }}
            variant="secondary"
            className={styles.mobileOnly}
            disabled={finalDisabled}
          >
            <CircleCheckBigIcon />
            Undo dismiss
          </Button>
        </>
      )}
      {/* Replied mentions are resolved with no further action (mirror of
          CommentActions, where a replied card renders no buttons). */}
      {!isDismissed && !isReplied && (
        <>
          <IconButton
            label="Dismiss this mention"
            onClick={() => {
              void handleDismiss()
            }}
            tooltip="Dismiss this mention if you don't want to act on it."
            variant="secondary"
            className={`${styles.dismissButton} ${styles.desktopOnly}`}
            name="dismiss-mention"
            aria-label="Dismiss this mention"
            disabled={finalDisabled}
          >
            <CircleCheckBigIcon />
          </IconButton>
          <Button
            onClick={() => {
              void handleDismiss()
            }}
            variant="secondary"
            className={styles.mobileOnly}
            disabled={finalDisabled}
          >
            <CircleCheckBigIcon />
            Dismiss
          </Button>
        </>
      )}
    </Flex>
  )
}

MentionActions.displayName = 'MentionActions'
